"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { NEWSLETTER_SIGNUP_STYLES } from "./constants";
import type { NewsletterSignupProps } from "./types";

const NEWSLETTER_COOKIE_NAME = "ticketon_newsletter_subscribed";
const NEWSLETTER_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
const RECAPTCHA_ACTION = "newsletter_subscribe";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
}

function setCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export const NewsletterSignup = React.forwardRef<
  HTMLElement,
  NewsletterSignupProps
>(
  (
    {
      className,
      title,
      description,
      emailLabel = "Email",
      emailPlaceholder,
      ctaLabel,
      ctaHref,
      imageUrl,
      disclaimer,
      loadingLabel,
      onSubmit,
      isLoading: externalIsLoading = false,
      onSubscribe,
      onSuccess,
      interests = [],
      pageViewData,
      apiBaseUrl = "",
      getRecaptchaToken,
      ...props
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isHidden, setIsHidden] = React.useState(true);

    React.useEffect(() => {
      const hasSubscribed = getCookie(NEWSLETTER_COOKIE_NAME) === "true";
      setIsHidden(hasSubscribed);
    }, []);

    const handleInternalSubscribe = React.useCallback(
      async (email: string) => {
        try {
          const reCaptchaToken = getRecaptchaToken
            ? await getRecaptchaToken(RECAPTCHA_ACTION)
            : "";

          const pageView = {
            path: pageViewData?.pathname || "/",
            urlParameters: pageViewData?.urlParameters || {},
            coreUserExternalId: null,
            latitude: pageViewData?.latitude || "",
            longitude: pageViewData?.longitude || "",
            ipAddress: pageViewData?.ip || "",
            city: pageViewData?.city || "",
            country: pageViewData?.country || "",
            locale: typeof navigator !== "undefined" ? navigator.language : "es-MX",
            referrer: pageViewData?.referrer || (typeof document !== "undefined" ? document.referrer : ""),
            objectId: null,
            viewType: null,
            currency: null,
            total: null,
            metadata: null,
          };

          const body = {
            email,
            reCaptchaToken,
            interests,
            pageView: btoa(JSON.stringify(pageView)),
          };

          const response = await fetch(`${apiBaseUrl}/api/newsletter/subscribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          setCookie(NEWSLETTER_COOKIE_NAME, "true", NEWSLETTER_COOKIE_MAX_AGE);
          return { success: true };
        } catch (error) {
          console.error("Newsletter subscription failed:", error);
          return { success: false, error: String(error) };
        }
      },
      [pageViewData, interests, apiBaseUrl, getRecaptchaToken]
    );

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // Legacy support
      if (onSubmit) {
        onSubmit(event);
        return;
      }

      const formData = new FormData(event.currentTarget);
      const email = formData.get("email") as string;

      if (!email || isLoading) return;

      setIsLoading(true);
      try {
        const result = onSubscribe
          ? await onSubscribe(email)
          : await handleInternalSubscribe(email);

        if (result.success) {
          setIsHidden(true);
          onSuccess?.();
        }
      } catch (error) {
        console.error("Newsletter subscription failed:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Use external loading state for legacy support
    const showLoading = onSubmit ? externalIsLoading : isLoading;
    const buttonLabel = showLoading && loadingLabel ? loadingLabel : ctaLabel;

    // Hide if already subscribed (only when using internal logic)
    if (!onSubmit && !onSubscribe && isHidden) {
      return null;
    }

    return (
      <section
        ref={ref}
        className={cn(NEWSLETTER_SIGNUP_STYLES.root, className)}
        {...props}
      >
        <div className={NEWSLETTER_SIGNUP_STYLES.card}>
          {imageUrl && (
            <div className={NEWSLETTER_SIGNUP_STYLES.imageWrapper}>
              <img
                src={imageUrl}
                alt=""
                aria-hidden="true"
                className={NEWSLETTER_SIGNUP_STYLES.image}
                loading="lazy"
              />
            </div>
          )}

          <div className={NEWSLETTER_SIGNUP_STYLES.content}>
            <h2 className={NEWSLETTER_SIGNUP_STYLES.title}>{title}</h2>
            <p className={NEWSLETTER_SIGNUP_STYLES.description}>{description}</p>

            <form
              onSubmit={handleSubmit}
              className={NEWSLETTER_SIGNUP_STYLES.form}
            >
              <label
                className={NEWSLETTER_SIGNUP_STYLES.emailLabel}
                htmlFor="newsletter-email"
              >
                {emailLabel}
              </label>
              <input
                id="newsletter-email"
                type="email"
                name="email"
                required
                placeholder={emailPlaceholder}
                className={cn(NEWSLETTER_SIGNUP_STYLES.input, showLoading && "opacity-50 cursor-not-allowed")}
                disabled={showLoading}
              />
              <button
                type="submit"
                className={cn(NEWSLETTER_SIGNUP_STYLES.cta, showLoading && "opacity-50 cursor-not-allowed")}
                disabled={showLoading}
              >
                {buttonLabel}
              </button>
            </form>

            {disclaimer && (
              <p className={NEWSLETTER_SIGNUP_STYLES.disclaimer}>
                {disclaimer}
              </p>
            )}
          </div>
        </div>
      </section>
    );
  }
);

NewsletterSignup.displayName = "NewsletterSignup";
